import { useState } from 'react';
import { getRouteApi, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApprovalSummary, RunDetail } from '@app/shared';
import { TERMINAL_RUN_STATUSES } from '@app/shared';
import {
  Banner,
  Button,
  Cluster,
  CodeBlock,
  Dialog,
  ErrorState,
  Icon,
  Icons,
  LoadingState,
  Metric,
  Panel,
  PaneHeader,
  Spinner,
  StatusBadge,
  Stack,
  Text,
  TextArea,
} from '../ui';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { useWorkspaceId } from '../lib/auth';
import { useScopedWorkspace } from '../lib/operatorWorkspace';
import { useOperationsStream } from '../lib/useOperationsStream';
import { isSendClassTool } from '../lib/sendClass';
import { durationMs, formatDuration } from '../lib/duration';
import { formatCost } from '../lib/formatCost';
import { RunStatusBadge } from '../components/RunStatusBadge';
import { RunOutputs } from '../components/RunOutputs';

/**
 * Run detail (§6.4, §6.5, §14) — the FULL-PAGE run view (#279), reached from the Runs
 * split-view preview via "View full details" (`/app/runs/$runId/full`). Leads with
 * OUTPUTS (issue #120): the research report / draft / artifact the run produced is the
 * first, most prominent thing. The step-by-step transcript (model turns, tool calls,
 * approvals, wakes) is a power-user detail moved behind a link to the full-screen
 * `/app/runs/$runId/transcript`.
 *
 * Kept here (not "advanced"): the header (status, agent, Started/Duration, Cancel, plus a
 * "Back to runs" control that returns to the list PRESERVING the operator's filters), a
 * pending-approval card that lets the operator approve/reject a WAITING_FOR_APPROVAL run
 * IN PLACE, the Waiting/resume card, and per-run cost/usage. Live via SSE.
 */

const TERMINAL: readonly string[] = TERMINAL_RUN_STATUSES;

const runFullRoute = getRouteApi('/app/runs/$runId/full');

export function RunDetailPage() {
  // #278: scope to the operator-selected workspace (the `ws` search param carried from the
  // runs list through the preview). `viewOnly` (a foreign workspace) hides the write
  // affordances — Cancel and the in-place approval — which the server keeps member-gated.
  const { ws } = runFullRoute.useSearch();
  const { workspaceId, viewOnly } = useScopedWorkspace(ws);
  const { runId } = useParams({ from: '/app/runs/$runId/full' });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // SSE only for the caller's own workspace; a foreign workspace falls back to non-live.
  useOperationsStream(viewOnly ? null : workspaceId);
  // #279: this is now a top-level page (not the split pane), so it owns its own SSE
  // subscription — the runs list is no longer mounted to keep the run fresh.

  const run = useQuery({
    queryKey: qk.run(workspaceId, runId),
    queryFn: () => api.getRun(workspaceId, runId),
  });

  const cancel = useMutation({
    mutationFn: () => api.cancelRun(workspaceId, runId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.run(workspaceId, runId) });
      await queryClient.invalidateQueries({ queryKey: qk.runs(workspaceId) });
    },
  });

  if (run.isLoading) return <LoadingState variant="skeleton" rows={6} label="Loading run…" />;
  if (run.isError) {
    return (
      <ErrorState
        description={run.error instanceof ApiError ? run.error.message : 'Could not load this run.'}
        onRetry={() => void run.refetch()}
      />
    );
  }

  const detail = run.data as RunDetail;
  // Cancel is member-gated; an operator viewing a foreign workspace is view-only.
  const cancellable = !viewOnly && !TERMINAL.includes(detail.run.status);
  const elapsed = formatDuration(durationMs(detail.run.startedAt, detail.run.endedAt));

  return (
    <Stack gap="5">
      {/* Header */}
      <PaneHeader
        title={`Run ${detail.run.id.slice(0, 8)}`}
        status={<RunStatusBadge status={detail.run.status} />}
        description={detail.run.progressSummary || undefined}
        actions={
          <Cluster gap="2" wrap>
            <Button
              variant="secondary"
              size="sm"
              iconStart={<Icon as={Icons.ArrowLeft} size="sm" />}
              // #279: return to the runs list preserving the operator's filters — carry
              // this page's search (outcome #263, agent, date window) straight back.
              onClick={() => void navigate({ to: '/app/runs', search: (prev) => prev })}
              data-testid="run-details-back"
            >
              Back to runs
            </Button>
            {cancellable && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => cancel.mutate()}
                loading={cancel.isPending}
                data-testid="cancel-run"
              >
                Cancel run
              </Button>
            )}
          </Cluster>
        }
        meta={
          <Cluster gap="4" wrap>
            {detail.agentName && (
              <Text size="sm" tone="secondary" data-testid="run-agent-name">
                Agent: {detail.agentName}
              </Text>
            )}
            {detail.run.startedAt && (
              <Text size="sm" tone="secondary" data-testid="run-started">
                Started {new Date(detail.run.startedAt).toLocaleString()}
              </Text>
            )}
            {elapsed && (
              <Text size="sm" tone="secondary" data-testid="run-duration">
                Duration {elapsed}
              </Text>
            )}
            {/* #299: when this run is on an email thread, jump to all runs on that thread
                (the runs list filtered to the thread, outcome 'all' so nothing is hidden). */}
            {detail.run.threadId && (
              <Button
                variant="ghost"
                size="sm"
                iconStart={<Icon as={Icons.GitBranch} size="sm" />}
                onClick={() =>
                  void navigate({
                    to: '/app/runs',
                    search: (prev) => ({
                      ...prev,
                      threadId: detail.run.threadId ?? undefined,
                      outcome: 'all',
                    }),
                  })
                }
                data-testid="run-thread-link"
              >
                View all runs on this thread
              </Button>
            )}
          </Cluster>
        }
      />

      {/* #300: a newer message arrived on this run's thread while it was parked — the run's
          draft/output may be outdated. Warn the operator and reuse the thread link. */}
      {detail.run.newerThreadMessage && (
        <Banner
          tone="warning"
          icon={Icons.AlertTriangle}
          data-testid="run-newer-thread-banner"
          action={
            detail.run.threadId ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  void navigate({
                    to: '/app/runs',
                    search: (prev) => ({
                      ...prev,
                      threadId: detail.run.threadId ?? undefined,
                      outcome: 'all',
                    }),
                  })
                }
                data-testid="run-newer-thread-link"
              >
                View all runs on this thread
              </Button>
            ) : undefined
          }
        >
          A newer message has arrived on this thread — this run's draft may be outdated.
        </Banner>
      )}

      {cancel.isError && (
        <Text tone="danger" size="sm">
          {cancel.error instanceof ApiError ? cancel.error.message : 'Cancel failed.'}
        </Text>
      )}

      {/* OUTPUTS LEAD (#120) */}
      <OutputsSection detail={detail} />

      <PromptSection detail={detail} />

      {/* Approving/rejecting is member-gated — omitted when viewing a foreign workspace. */}
      {!viewOnly && <PendingApprovalCard runId={runId} runStatus={detail.run.status} />}

      {/* Cost & usage */}
      <Panel title="Cost & usage">
        <Cluster gap="8" wrap>
          <Metric
            label="Total cost"
            icon={Icons.DollarSign}
            value={formatCost(detail.usage.totalCostUsd)}
            valueTestId="usage-cost"
          />
          <Metric label="Tokens in" icon={Icons.ArrowDownToLine} value={detail.usage.totalTokensIn.toLocaleString()} />
          <Metric label="Tokens out" icon={Icons.ArrowUpFromLine} value={detail.usage.totalTokensOut.toLocaleString()} />
        </Cluster>
      </Panel>

      {detail.run.status === 'WAITING' && detail.run.resumeAt && (
        <Panel title="Waiting">
          <Text tone="secondary" size="sm" data-testid="run-resume-at">
            Scheduled to resume at {new Date(detail.run.resumeAt).toLocaleString()}.
          </Text>
        </Panel>
      )}

      {detail.run.endedReason && (
        <Panel title="Outcome">
          <Text tone="secondary" size="sm">
            {detail.run.endedReason}
          </Text>
        </Panel>
      )}

      {/* Transcript — the deeper "advanced" view on its own route. */}
      <Panel
        title="Step-by-step transcript"
        description="Model turns, tool calls, approvals, and wakes — the full audit trail."
        actions={
          <Button
            variant="secondary"
            size="sm"
            iconStart={<Icon as={Icons.ScrollText} size="sm" />}
            onClick={() =>
              void navigate({
                to: '/app/runs/$runId/transcript',
                params: { runId },
                search: (prev) => prev,
              })
            }
            data-testid="view-transcript"
          >
            View transcript
          </Button>
        }
      >
        <Text tone="muted" size="sm">
          Open the full transcript to inspect every step of this run.
        </Text>
      </Panel>
    </Stack>
  );
}

/**
 * The Outputs section — the lead of the run page (#120). Renders the report/draft/
 * artifact richly if the run produced any; otherwise a friendly in-progress state so a
 * running or output-less run reads as "working", not empty.
 */
function OutputsSection({ detail }: { detail: RunDetail }) {
  const hasOutputs = detail.artifacts.length > 0;
  const inProgress = !TERMINAL.includes(detail.run.status);
  return (
    <Panel title="Outputs" data-testid="artifacts-card">
      {hasOutputs ? (
        <RunOutputs artifacts={detail.artifacts} />
      ) : (
        <Stack gap="3" align="center" data-testid="outputs-in-progress">
          {inProgress && <Spinner size="md" />}
          <Text tone="secondary">
            {inProgress ? 'Working on it — outputs will appear here.' : 'This run produced no outputs.'}
          </Text>
          {detail.run.progressSummary && (
            <Text tone="secondary" size="sm" data-testid="outputs-progress-summary">
              {detail.run.progressSummary}
            </Text>
          )}
        </Stack>
      )}
    </Panel>
  );
}

/**
 * When a prompt is "large" enough to clamp on the full page (#283). The preview pane
 * clamps unconditionally (#279); here the whole page has room, so we only collapse a
 * prompt that would otherwise dominate — a short prompt shows in full with no toggle.
 * A prompt is large if it has many lines OR is very long overall.
 */
const PROMPT_COLLAPSE_LINE_THRESHOLD = 16;
const PROMPT_COLLAPSE_CHAR_THRESHOLD = 1200;
/** Lines shown when a large prompt is collapsed. */
const PROMPT_COLLAPSED_LINES = 12;

function isLargePrompt(prompt: string): boolean {
  const lineCount = prompt.split('\n').length;
  return lineCount > PROMPT_COLLAPSE_LINE_THRESHOLD || prompt.length > PROMPT_COLLAPSE_CHAR_THRESHOLD;
}

/**
 * The prompt this run used (§14, #193). A `templated` run carries a per-run `taskPrompt`
 * — the operator-edited prompt for THIS run — which takes priority and is tagged
 * "Per-run prompt". Absent that, falls back to `agentPrompt`. Omitted when both are null.
 *
 * #283: a large prompt is CLAMPED to a sensible height by default with a "Show full
 * prompt" / "Show less" toggle that expands it in place, so it never dominates the page.
 * A short prompt renders fully with no toggle.
 */
function PromptSection({ detail }: { detail: RunDetail }) {
  const perRun = detail.taskPrompt;
  const prompt = perRun ?? detail.agentPrompt;
  const [expanded, setExpanded] = useState(false);
  if (!prompt) return null;
  const collapsible = isLargePrompt(prompt);
  const clamped = collapsible && !expanded;
  return (
    <Panel
      title="Prompt"
      data-testid="run-prompt-card"
      actions={
        perRun ? (
          <StatusBadge
            tone="brand"
            size="sm"
            label="Per-run prompt"
            data-testid="run-prompt-per-run-tag"
          />
        ) : undefined
      }
    >
      <Stack gap="3" align="start">
        <Text
          size="sm"
          data-testid="run-prompt"
          style={
            clamped
              ? {
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: PROMPT_COLLAPSED_LINES,
                  overflow: 'hidden',
                  whiteSpace: 'pre-wrap',
                }
              : { whiteSpace: 'pre-wrap' }
          }
        >
          {prompt}
        </Text>
        {collapsible && (
          <Button
            variant="ghost"
            size="sm"
            iconStart={<Icon as={expanded ? Icons.ChevronUp : Icons.ChevronDown} size="sm" />}
            onClick={() => setExpanded((v) => !v)}
            data-testid="run-prompt-expand"
            aria-expanded={expanded}
          >
            {expanded ? 'Show less' : 'Show full prompt'}
          </Button>
        )}
      </Stack>
    </Panel>
  );
}

/**
 * Pending-approval card (§10) — a WAITING_FOR_APPROVAL run MUST be actionable from the
 * run page (#120). We find the run's pending approval from the approvals list (keyed by
 * `runId`) and let the operator approve/reject in place; the platform (not the model)
 * executes an approved call with exactly the pinned arguments. A link opens the approval
 * detail for the full exact-arguments / content-snapshot review (§14).
 */
function PendingApprovalCard({ runId, runStatus }: { runId: string; runStatus: string }) {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);

  const enabled = runStatus === 'WAITING_FOR_APPROVAL';
  const approvals = useQuery({
    queryKey: qk.approvals(workspaceId),
    queryFn: () => api.listApprovals(workspaceId, { status: 'pending', pageSize: 100 }),
    enabled,
  });

  const pending: ApprovalSummary | undefined = approvals.data?.data.find(
    (a) => a.runId === runId && a.status === 'pending',
  );
  const sendClass = isSendClassTool(pending?.tool);

  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => {
      if (!pending) throw new Error('No pending approval.');
      return api.decideApproval(workspaceId, pending.id, decision, note || undefined);
    },
    onSuccess: async (res, decision) => {
      const executed = res.status === 'approved';
      setBanner({
        kind: decision === 'approved' && !executed ? 'error' : 'success',
        text:
          decision === 'rejected'
            ? 'Rejected. The run was woken with your note.'
            : executed
              ? 'Approved. The platform executed the action with exactly these arguments.'
              : `Approved, but the platform could not execute it (status: ${res.status}).`,
      });
      await queryClient.invalidateQueries({ queryKey: qk.approvals(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: qk.run(workspaceId, runId) });
    },
    onError: (err) =>
      setBanner({ kind: 'error', text: err instanceof ApiError ? err.message : 'Decision failed.' }),
  });

  // Keep the card mounted while a decision banner is showing, even though approving flips
  // the run out of WAITING_FOR_APPROVAL (which would otherwise unmount the card and lose
  // the "executed" confirmation). The form is gated on `enabled`, so once decided only
  // the banner remains.
  if (!enabled && !banner) return null;

  return (
    <Panel title="Approval required" data-testid="pending-approval-card">
      <Stack gap="4">
        {banner && (
          <Banner
            tone={banner.kind === 'error' ? 'danger' : 'success'}
            onDismiss={() => setBanner(null)}
            data-testid="approval-banner"
          >
            {banner.text}
          </Banner>
        )}
        {!enabled ? null : approvals.isPending ? (
          <Text tone="secondary" size="sm">
            Loading the pending approval…
          </Text>
        ) : pending ? (
          <Stack gap="3">
            <Text tone="secondary" size="sm">
              This run is paused for approval of <CodeBlock inline>{pending.tool}</CodeBlock>.{' '}
              {pending.reason}
            </Text>
            <TextArea
              aria-label="Note (optional)"
              placeholder="Note (optional)"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <Cluster gap="3" wrap>
              <Button
                variant="primary"
                onClick={() => (sendClass ? setSendConfirmOpen(true) : decide.mutate('approved'))}
                loading={decide.isPending}
                data-testid="approve"
              >
                {sendClass ? 'Approve & send' : 'Approve'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => decide.mutate('rejected')}
                disabled={decide.isPending}
                data-testid="reject"
              >
                Reject
              </Button>
              <Button
                variant="ghost"
                onClick={() =>
                  void navigate({ to: '/app/approvals/$approvalId', params: { approvalId: pending.id } })
                }
                data-testid="review-approval"
              >
                Review exact arguments
              </Button>
            </Cluster>
          </Stack>
        ) : (
          <Text tone="secondary" size="sm">
            This run is awaiting approval. Open the approvals inbox to act on it.
          </Text>
        )}
      </Stack>

      {/* Send-class confirm (email.send etc.). Approving executes the send with exactly
          the pinned arguments — irreversible — so it takes one explicit confirm. */}
      <Dialog
        open={sendConfirmOpen}
        onClose={() => setSendConfirmOpen(false)}
        title="Send this now?"
        size="sm"
        data-testid="send-confirm-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSendConfirmOpen(false)} data-testid="send-cancel">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setSendConfirmOpen(false);
                decide.mutate('approved');
              }}
              loading={decide.isPending}
              data-testid="confirm-approve-send"
            >
              Approve &amp; send
            </Button>
          </>
        }
      >
        <Text tone="secondary" size="md">
          Approving <CodeBlock inline>{pending?.tool}</CodeBlock> <strong>sends immediately</strong>{' '}
          with exactly the pinned arguments. This is a real, irreversible action taken on your
          behalf — it cannot be unsent.
        </Text>
      </Dialog>
    </Panel>
  );
}
